import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, X } from "lucide-react";

/**
 * "Forward to a colleague" button + modal, rendered next to the
 * Currency Switcher on the Costs tab.
 *
 * A verified client (one who's passed the email gate) can invite a
 * SAME-DOMAIN colleague onto the proposal link. The backend
 * (POST /api/public/proposals/{token}/forward) validates the
 * same-domain rule server-side using the client's signed gate
 * cookie, then adds the colleague to the recipient allow-list
 * (idempotent via $addToSet).
 *
 * On success we hand the OS a mailto: draft pre-filled with:
 *
 *   Subject: Medicys Proposal for: <Project Reference>
 *   Body:    Hi <colleague-first-name>,
 *
 *            Please see below a link to the Medicys proposal for: <ref>
 *
 *            <share URL>
 *
 *            Thanks
 *
 * The sender's own name/signature is intentionally left to their
 * email client (Outlook / Letsignit will append their signature on
 * send).
 */
export function ForwardToColleagueButton({
  apiBaseUrl,
  token,
  senderEmail,
}) {
  const [open, setOpen] = useState(false);
  const [colleagueEmail, setColleagueEmail] = useState("");
  const [colleagueFirstName, setColleagueFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const senderDomain = (senderEmail || "").split("@")[1]?.toLowerCase() || "";

  const openModal = () => {
    setOpen(true);
    setError(null);
  };
  const closeModal = () => {
    if (submitting) return;
    setOpen(false);
    setError(null);
  };

  const validateClientSide = () => {
    const em = (colleagueEmail || "").trim().toLowerCase();
    const first = (colleagueFirstName || "").trim();
    if (!first) return "Please enter your colleague's first name.";
    if (!em) return "Please enter your colleague's email address.";
    const emailRe = /^[A-Za-z0-9._%+\-]+@([A-Za-z0-9][A-Za-z0-9\-]{0,62}\.)+[A-Za-z]{2,}$/;
    if (!emailRe.test(em)) return "That doesn't look like a valid email address.";
    if (em === (senderEmail || "").toLowerCase()) {
      return "That's your own email address.";
    }
    const dom = em.split("@")[1];
    if (!dom) return "Missing domain in the email address.";
    // Medicys is always allowed; otherwise domain must match sender's.
    if (dom !== "medicysltd.co.uk" && dom !== senderDomain) {
      return `You can only forward to colleagues on @${senderDomain}.`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const localErr = validateClientSide();
    if (localErr) {
      setError(localErr);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `${apiBaseUrl}/api/public/proposals/${token}/forward`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            colleague_email: colleagueEmail.trim(),
            colleague_first_name: colleagueFirstName.trim(),
          }),
        }
      );
      if (!res.ok) {
        let msg = "We couldn't send that invite.";
        try {
          const j = await res.json();
          const reason = j?.detail?.reason || "";
          if (reason === "cross_domain") {
            msg = `You can only forward to colleagues on @${senderDomain}.`;
          } else if (reason === "personal_domain") {
            msg = "Personal-email addresses (gmail, outlook, etc.) aren't allowed.";
          } else if (reason === "self") {
            msg = "That's your own email address.";
          } else if (reason === "invalid_email") {
            msg = "That doesn't look like a valid email address.";
          } else if (reason === "gate_cookie_missing") {
            msg = "Your session has expired. Please refresh the page.";
          }
        } catch {
          /* fall through to default msg */
        }
        setError(msg);
        setSubmitting(false);
        return;
      }
      const data = await res.json();
      // Open the OS mail client with the pre-filled draft.
      const mailto = buildMailtoUrl(
        colleagueEmail.trim(),
        data.subject,
        data.body_plain
      );
      // Trigger the mailto in a way that Outlook picks up cleanly.
      window.location.href = mailto;
      toast.success(
        `Invite sent - ${colleagueFirstName.trim()} can now open the proposal.`
      );
      setOpen(false);
      setColleagueEmail("");
      setColleagueFirstName("");
    } catch (err) {
      setError("Network error. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="forward-colleague-btn"
        title={
          senderEmail
            ? `Forward this proposal to a colleague on @${senderDomain}`
            : "Sign in to forward this proposal"
        }
        className="no-print inline-flex items-center gap-2 rounded-full bg-[#0d9488] hover:bg-[#0f766e] text-white shadow-sm hover:shadow-md px-4 py-2 text-[11px] font-mono font-semibold tracking-[0.14em] uppercase transition-all"
      >
        <Send className="w-3.5 h-3.5" aria-hidden />
        <span className="hidden xl:inline">Forward to a colleague</span>
        <span className="xl:hidden">Forward</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="forward-colleague-modal"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative"
            onClick={(ev) => ev.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeModal}
              data-testid="forward-colleague-close"
              className="absolute top-3 right-3 text-stone-400 hover:text-stone-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-[#1B4F8A] mb-1">
              Forward to a colleague
            </h3>
            <p className="text-xs text-stone-600 leading-relaxed mb-4">
              We&apos;ll add your colleague&apos;s address to the allow-list
              and open an email draft with the proposal link pre-filled.
              {senderDomain && (
                <>
                  {" "}
                  Same domain only:{" "}
                  <span className="font-mono">@{senderDomain}</span>.
                </>
              )}
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label
                  htmlFor="forward-colleague-first-name"
                  className="block text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500 mb-1"
                >
                  Colleague&apos;s first name
                </label>
                <input
                  id="forward-colleague-first-name"
                  type="text"
                  value={colleagueFirstName}
                  onChange={(e) => setColleagueFirstName(e.target.value)}
                  data-testid="forward-colleague-first-name"
                  placeholder="Sarah"
                  maxLength={80}
                  autoFocus
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:border-[#1B4F8A] focus:ring-1 focus:ring-[#1B4F8A] outline-none"
                />
              </div>
              <div>
                <label
                  htmlFor="forward-colleague-email"
                  className="block text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500 mb-1"
                >
                  Colleague&apos;s work email
                </label>
                <input
                  id="forward-colleague-email"
                  type="email"
                  value={colleagueEmail}
                  onChange={(e) => setColleagueEmail(e.target.value)}
                  data-testid="forward-colleague-email"
                  placeholder={
                    senderDomain ? `sarah@${senderDomain}` : "sarah@company.com"
                  }
                  maxLength={254}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:border-[#1B4F8A] focus:ring-1 focus:ring-[#1B4F8A] outline-none font-mono"
                />
              </div>
              {error && (
                <p
                  className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                  data-testid="forward-colleague-error"
                >
                  {error}
                </p>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={submitting}
                  data-testid="forward-colleague-cancel"
                  className="text-xs font-mono uppercase tracking-[0.12em] text-stone-600 hover:text-stone-900 px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="forward-colleague-submit"
                  className="inline-flex items-center gap-2 bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-60 disabled:cursor-progress text-white text-xs font-mono uppercase tracking-[0.12em] font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  {submitting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Send className="w-3.5 h-3.5" aria-hidden />
                  )}
                  {submitting ? "Sending…" : "Send invite"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}


// mailto: bodies are percent-encoded per RFC 6068. Newlines survive
// as %0D%0A (Outlook is strict about CRLF here).
function buildMailtoUrl(to, subject, body) {
  const encode = (s) => encodeURIComponent(s || "").replace(/%20/g, "%20");
  const bodyCrlf = (body || "").replace(/\r?\n/g, "\r\n");
  return (
    `mailto:${encodeURIComponent(to)}` +
    `?subject=${encode(subject)}` +
    `&body=${encode(bodyCrlf)}`
  );
}
